import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage first, default to English
    return localStorage.getItem('language') || 'en';
  });

  const toggleLanguage = () => {
    const newLanguage = language === 'en' ? 'es' : 'en';
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  const t = (key) => {
    return translations[language]?.[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const translations = {
  en: {
    // Header
    'nav.home': 'Home',
    'nav.services': 'Services',
    'nav.gallery': 'Gallery',
    'nav.testimonials': 'Testimonials',
    'nav.contact': 'Contact',
    
    // Hero
    'hero.badge': 'Years of Excellence',
    'hero.title1': 'Transform Your',
    'hero.title2': 'Home Into Reality',
    'hero.subtitle': 'Professional construction and repair services for homes in',
    'hero.freeEstimates': 'Free Estimates',
    'hero.satisfactionGuaranteed': 'Satisfaction Guaranteed',
    'hero.projectsCompleted': 'Projects Completed',
    'hero.ctaQuote': 'Request Free Quote',
    'hero.yearsExperience': 'Years of Experience',
    'hero.satisfaction': 'Client Satisfaction',
    'hero.servicesSpecialized': 'Specialized Services',
    
    // Services
    'services.badge': 'Our Services',
    'services.title': 'Complete Solutions For Your',
    'services.titleHighlight': 'Home',
    'services.subtitle': 'We offer a wide range of construction and repair services with the highest quality and professionalism',
    'services.plumbing': 'Plumbing',
    'services.plumbing.desc': 'Residential and commercial plumbing system repair and installation',
    'services.electrical': 'Electrical',
    'services.electrical.desc': 'Complete electrical services, certified installations and repairs',
    'services.painting': 'Painting',
    'services.painting.desc': 'High-quality interior and exterior painting with professional finishes',
    'services.masonry': 'Masonry',
    'services.masonry.desc': 'Masonry work, bricks, blocks and concrete',
    'services.flooring': 'Flooring',
    'services.flooring.desc': 'Installation of all types of flooring: wood, laminate, ceramic and more',
    'services.drywall': 'Drywall Repairs',
    'services.drywall.desc': 'Professional drywall panel repair and installation',
    'services.roofing': 'Roof Repairs',
    'services.roofing.desc': 'Residential and commercial roof repair and installation',
    'services.molding': 'Molding and Trims',
    'services.molding.desc': 'Installation of decorative moldings and fine finishes',
    'services.moreInfo': 'More information',
    'services.notFound': 'Can\'t find the service you\'re looking for?',
    'services.contactMore': 'Contact Us for More Services',
    
    // Why Choose Us
    'why.badge': 'Why Choose Us',
    'why.title': 'Your Satisfaction is Our',
    'why.titleHighlight': 'Priority',
    'why.subtitle': 'Committed to excellence in every project we undertake',
    'why.experience': '15+ Years of Experience',
    'why.experience.desc': 'Over a decade of experience in residential repairs and construction',
    'why.freeQuotes': 'Free Quotes',
    'why.freeQuotes.desc': 'No-commitment estimates for all our services',
    'why.guaranteed': 'Satisfaction Guaranteed',
    'why.guaranteed.desc': 'Your satisfaction is our priority. Guaranteed work',
    'why.certified': 'Certified Professionals',
    'why.certified.desc': 'Team of certified and licensed technicians',
    
    // Gallery
    'gallery.badge': 'Our Projects',
    'gallery.title': 'Gallery of',
    'gallery.titleHighlight': 'Work',
    'gallery.subtitle': 'Explore some of our successfully completed projects',
    'gallery.all': 'All',
    
    // Testimonials
    'testimonials.badge': 'Testimonials',
    'testimonials.title': 'What Our',
    'testimonials.titleHighlight': 'Clients Say',
    'testimonials.subtitle': 'The satisfaction of our clients is our best credential',
    'testimonials.ready': 'Ready to Start Your Project?',
    'testimonials.join': 'Join hundreds of satisfied customers and transform your home today',
    'testimonials.requestQuote': 'Request Free Quote',
    
    // Contact
    'contact.badge': 'Contact Us',
    'contact.title': 'Request Your',
    'contact.titleHighlight': 'Free Quote',
    'contact.subtitle': 'We are ready to help you with your next project. Contact us today',
    'contact.info': 'Contact Information',
    'contact.info.desc': 'Contact us by any of these means and we will respond as soon as possible',
    'contact.phone': 'Phone',
    'contact.phone.desc': 'Call us for immediate quote',
    'contact.email': 'Email',
    'contact.email.desc': 'Send us an email',
    'contact.serviceArea': 'Service Area',
    'contact.serviceArea.desc': 'Services throughout the region',
    'contact.hours': 'Hours',
    'contact.hours.monFri': 'Mon - Fri: 7:00 AM - 6:00 PM',
    'contact.hours.sat': 'Saturday: 8:00 AM - 4:00 PM',
    'contact.hours.emergency': 'Available for emergencies 24/7',
    'contact.form.title': 'Send Us a Message',
    'contact.form.name': 'Full Name',
    'contact.form.email': 'Email',
    'contact.form.phone': 'Phone',
    'contact.form.service': 'Service of Interest',
    'contact.form.service.select': 'Select a service',
    'contact.form.service.plumbing': 'Plumbing',
    'contact.form.service.electrical': 'Electrical',
    'contact.form.service.painting': 'Painting',
    'contact.form.service.masonry': 'Masonry',
    'contact.form.service.flooring': 'Flooring',
    'contact.form.service.drywall': 'Drywall Repair',
    'contact.form.service.roofing': 'Roof Repair',
    'contact.form.service.molding': 'Molding and Trim',
    'contact.form.service.other': 'Other',
    'contact.form.message': 'Message',
    'contact.form.message.placeholder': 'Tell us about your project...',
    'contact.form.submit': 'Send Message',
    'contact.form.submitting': 'Sending...',
    'contact.form.success': 'Message Sent',
    'contact.form.successMsg': 'Message sent successfully! We will contact you soon.',
    'contact.form.error': 'Error sending message. Please try again.',
    
    // Footer
    'footer.quickLinks': 'Quick Links',
    'footer.ourServices': 'Our Services',
    'footer.contactUs': 'Contact Us',
    'footer.callUs': 'Call Us',
    'footer.location': 'Location',
    'footer.rights': 'All rights reserved.',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    
    // WhatsApp
    'whatsapp.tooltip.title': 'Need help?',
    'whatsapp.tooltip.desc': 'Chat with us on WhatsApp for immediate response',
    'whatsapp.chat': 'Chat with us',
    'whatsapp.message': 'Hello! I\'m interested in getting more information about your construction and repair services.',
    
    // Popup
    'popup.badge': 'SPECIAL OFFER!',
    'popup.discount': '10% OFF',
    'popup.subtitle': 'On Your First Service',
    'popup.limited': 'Limited time offer!',
    'popup.title': 'Save on Any of Our Services',
    'popup.service1': 'Plumbing, Electrical, Painting',
    'popup.service2': 'Masonry, Flooring, Drywall',
    'popup.service3': 'Roofing, Molding and more...',
    'popup.years': 'Years',
    'popup.projects': 'Projects',
    'popup.satisfaction': 'Satisfaction',
    'popup.cta': 'Request My Discount Now',
    'popup.call': 'Call',
    'popup.disclaimer': '* Valid for new customers only. Not cumulative with other offers.',
  },
  es: {
    // Header
    'nav.home': 'Inicio',
    'nav.services': 'Servicios',
    'nav.gallery': 'Galería',
    'nav.testimonials': 'Testimonios',
    'nav.contact': 'Contacto',
    
    // Hero
    'hero.badge': 'Años de Excelencia',
    'hero.title1': 'Transformamos Tu',
    'hero.title2': 'Hogar en Realidad',
    'hero.subtitle': 'Servicios profesionales de construcción y reparación para hogares en',
    'hero.freeEstimates': 'Presupuestos Gratuitos',
    'hero.satisfactionGuaranteed': 'Satisfacción Garantizada',
    'hero.projectsCompleted': 'Proyectos Completados',
    'hero.ctaQuote': 'Solicitar Presupuesto Gratis',
    'hero.yearsExperience': 'Años de Experiencia',
    'hero.satisfaction': 'Satisfacción Cliente',
    'hero.servicesSpecialized': 'Servicios Especializados',
    
    // Services
    'services.badge': 'Nuestros Servicios',
    'services.title': 'Soluciones Completas Para Tu',
    'services.titleHighlight': 'Hogar',
    'services.subtitle': 'Ofrecemos una amplia gama de servicios de construcción y reparación con la más alta calidad y profesionalismo',
    'services.plumbing': 'Plomería',
    'services.plumbing.desc': 'Reparación y instalación de sistemas de plomería residencial y comercial',
    'services.electrical': 'Electricidad',
    'services.electrical.desc': 'Servicios eléctricos completos, instalaciones y reparaciones certificadas',
    'services.painting': 'Pintura',
    'services.painting.desc': 'Pintura interior y exterior de alta calidad con acabados profesionales',
    'services.masonry': 'Albañilería',
    'services.masonry.desc': 'Trabajos de albañilería, ladrillos, bloques y concreto',
    'services.flooring': 'Pisos',
    'services.flooring.desc': 'Instalación de todo tipo de pisos: madera, laminado, cerámica y más',
    'services.drywall': 'Reparación de Drywall',
    'services.drywall.desc': 'Reparación y instalación profesional de paneles de yeso',
    'services.roofing': 'Reparación de Techos',
    'services.roofing.desc': 'Reparación e instalación de techos residenciales y comerciales',
    'services.molding': 'Molduras y Acabados',
    'services.molding.desc': 'Instalación de molduras decorativas y acabados finos',
    'services.moreInfo': 'Más información',
    'services.notFound': '¿No encuentras el servicio que buscas?',
    'services.contactMore': 'Contáctanos para Más Servicios',
    
    // Why Choose Us
    'why.badge': 'Por Qué Elegirnos',
    'why.title': 'Tu Satisfacción es Nuestra',
    'why.titleHighlight': 'Prioridad',
    'why.subtitle': 'Comprometidos con la excelencia en cada proyecto que realizamos',
    'why.experience': '15+ Años de Experiencia',
    'why.experience.desc': 'Más de una década de experiencia en reparaciones y construcción residencial',
    'why.freeQuotes': 'Presupuestos Gratuitos',
    'why.freeQuotes.desc': 'Estimaciones sin compromiso para todos nuestros servicios',
    'why.guaranteed': 'Satisfacción Garantizada',
    'why.guaranteed.desc': 'Tu satisfacción es nuestra prioridad. Trabajo garantizado',
    'why.certified': 'Profesionales Certificados',
    'why.certified.desc': 'Equipo de técnicos certificados y con licencia',
    
    // Gallery
    'gallery.badge': 'Nuestros Proyectos',
    'gallery.title': 'Galería de',
    'gallery.titleHighlight': 'Trabajos',
    'gallery.subtitle': 'Explora algunos de nuestros proyectos completados con éxito',
    'gallery.all': 'Todos',
    
    // Testimonials
    'testimonials.badge': 'Testimonios',
    'testimonials.title': 'Lo Que Dicen Nuestros',
    'testimonials.titleHighlight': 'Clientes',
    'testimonials.subtitle': 'La satisfacción de nuestros clientes es nuestra mejor carta de presentación',
    'testimonials.ready': '¿Listo para Empezar tu Proyecto?',
    'testimonials.join': 'Únete a cientos de clientes satisfechos y transforma tu hogar hoy',
    'testimonials.requestQuote': 'Solicitar Presupuesto Gratis',
    
    // Contact
    'contact.badge': 'Contáctanos',
    'contact.title': 'Solicita Tu',
    'contact.titleHighlight': 'Presupuesto Gratis',
    'contact.subtitle': 'Estamos listos para ayudarte con tu próximo proyecto. Contáctanos hoy mismo',
    'contact.info': 'Información de Contacto',
    'contact.info.desc': 'Contáctanos por cualquiera de estos medios y te responderemos lo antes posible',
    'contact.phone': 'Teléfono',
    'contact.phone.desc': 'Llámanos para presupuesto inmediato',
    'contact.email': 'Email',
    'contact.email.desc': 'Envíanos un correo electrónico',
    'contact.serviceArea': 'Área de Servicio',
    'contact.serviceArea.desc': 'Servicios en toda la región',
    'contact.hours': 'Horario',
    'contact.hours.monFri': 'Lun - Vie: 7:00 AM - 6:00 PM',
    'contact.hours.sat': 'Sábado: 8:00 AM - 4:00 PM',
    'contact.hours.emergency': 'Disponibles para emergencias 24/7',
    'contact.form.title': 'Envíanos un Mensaje',
    'contact.form.name': 'Nombre Completo',
    'contact.form.email': 'Correo Electrónico',
    'contact.form.phone': 'Teléfono',
    'contact.form.service': 'Servicio de Interés',
    'contact.form.service.select': 'Selecciona un servicio',
    'contact.form.service.plumbing': 'Plomería',
    'contact.form.service.electrical': 'Electricidad',
    'contact.form.service.painting': 'Pintura',
    'contact.form.service.masonry': 'Albañilería',
    'contact.form.service.flooring': 'Pisos',
    'contact.form.service.drywall': 'Reparación de Drywall',
    'contact.form.service.roofing': 'Reparación de Techos',
    'contact.form.service.molding': 'Molduras y Acabados',
    'contact.form.service.other': 'Otro',
    'contact.form.message': 'Mensaje',
    'contact.form.message.placeholder': 'Cuéntanos sobre tu proyecto...',
    'contact.form.submit': 'Enviar Mensaje',
    'contact.form.submitting': 'Enviando...',
    'contact.form.success': 'Mensaje Enviado',
    'contact.form.successMsg': '¡Mensaje enviado con éxito! Nos pondremos en contacto pronto.',
    'contact.form.error': 'Error al enviar el mensaje. Por favor intenta de nuevo.',
    
    // Footer
    'footer.quickLinks': 'Enlaces Rápidos',
    'footer.ourServices': 'Nuestros Servicios',
    'footer.contactUs': 'Contáctanos',
    'footer.callUs': 'Llámanos',
    'footer.location': 'Ubicación',
    'footer.rights': 'Todos los derechos reservados.',
    'footer.privacy': 'Política de Privacidad',
    'footer.terms': 'Términos de Servicio',
    
    // WhatsApp
    'whatsapp.tooltip.title': '¿Necesitas ayuda?',
    'whatsapp.tooltip.desc': 'Chatea con nosotros en WhatsApp para respuesta inmediata',
    'whatsapp.chat': 'Chatea con nosotros',
    'whatsapp.message': '¡Hola! Me interesa obtener más información sobre sus servicios de construcción y reparación.',
    
    // Popup
    'popup.badge': '¡OFERTA ESPECIAL!',
    'popup.discount': '10% OFF',
    'popup.subtitle': 'En Tu Primer Servicio',
    'popup.limited': '¡Oferta por tiempo limitado!',
    'popup.title': 'Ahorra en Cualquiera de Nuestros Servicios',
    'popup.service1': 'Plomería, Electricidad, Pintura',
    'popup.service2': 'Albañilería, Pisos, Drywall',
    'popup.service3': 'Techos, Molduras y más...',
    'popup.years': 'Años',
    'popup.projects': 'Proyectos',
    'popup.satisfaction': 'Satisfacción',
    'popup.cta': 'Solicitar Mi Descuento Ahora',
    'popup.call': 'Llamar',
    'popup.disclaimer': '* Válido solo para nuevos clientes. No acumulable con otras ofertas.',
  }
};
